import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Feather, Copy, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devToken, setDevToken] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot", { email });
      setSent(true);
      if (data.reset_link_token) {
        setDevToken(data.reset_link_token);
        toast.success("Dev mode: reset link generated below.");
      } else {
        toast.success("Check your inbox for the reset link.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const resetUrl = devToken ? `${window.location.origin}/reset-password?token=${devToken}` : "";
  const copy = async () => { await navigator.clipboard.writeText(resetUrl); toast.success("Link copied"); };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="w-8 h-8 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] flex items-center justify-center"><Feather size={16} /></span>
          <span className="font-display text-xl">Humanly</span>
        </Link>
        <div className="surface p-8">
          {!sent ? (
            <>
              <h1 className="font-display text-3xl mb-2">Reset your password</h1>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={submit} className="space-y-4">
                <input
                  data-testid="forgot-email"
                  type="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-white border border-[hsl(var(--border))] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/30 focus:border-[hsl(var(--primary))]"
                />
                <button data-testid="forgot-submit" type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
                  {loading ? "Sending…" : "Send reset link"}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="w-14 h-14 rounded-full bg-[hsl(var(--secondary))] mx-auto flex items-center justify-center mb-4">
                <MailCheck className="text-[hsl(var(--primary))]" size={26} />
              </div>
              <h1 className="font-display text-3xl mb-2 text-center">Check your inbox</h1>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6 text-center">
                If an account exists for <span className="font-semibold text-[hsl(var(--foreground))]">{email}</span>, we've sent a reset link. It expires in 1 hour.
              </p>

              {devToken && (
                <div className="rounded-xl bg-[hsl(var(--secondary))] p-4 mb-4">
                  <div className="label-eyebrow mb-2">Dev mode reset link</div>
                  <div className="flex items-center gap-2">
                    <input readOnly value={resetUrl} className="flex-1 text-xs bg-white border border-[hsl(var(--border))] rounded-lg px-3 py-2 font-mono-editor truncate" />
                    <button onClick={copy} className="btn-ghost px-3 py-2 text-xs flex items-center gap-1"><Copy size={12} /> Copy</button>
                  </div>
                </div>
              )}

              <div className="text-sm text-center">
                <button onClick={() => { setSent(false); setDevToken(null); }} className="link-underline text-[hsl(var(--primary))] font-semibold">
                  Try a different email
                </button>
              </div>
            </>
          )}
          <div className="mt-6 text-sm text-center">
            <Link to="/login" className="link-underline text-[hsl(var(--muted-foreground))]">Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
